import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../components/Toast";
import { compareByStart, compareByStartDesc, isEventUpcoming } from "../../../lib/format";
import { EventService } from "../../../../../services/eventService";
import { exportClubSemesterCsv } from "../../../../../services/attendanceExportService";
import { clearPending, markPending } from "../../../lib/pendingWork";
import { msg } from "./eventUtils";
import { signedInAs } from "../../../lib/session";

const upsert = (list, incoming) => {
  const ids = new Set(incoming.map((e) => e.id));
  return [...incoming, ...list.filter((e) => !ids.has(e.id))];
};
/** Events tab data + actions (AdminScreen list :689, create :959, edit :1164, delete :2033). */
export default function useManageEvents({ club, onClubChanged }) {
  const { user } = useAuth();
  const toast = useToast();
  const uid = user?.uid;
  const clubId = club?.id;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("upcoming");
  const [tick, setTick] = useState(0);
  const [form, setForm] = useState(null); // { event } while open
  const [created, setCreated] = useState(null); // { event, extra }
  const [qrEvent, setQrEvent] = useState(null);
  const [attend, setAttend] = useState(null); // { event, mode }
  const [toDelete, setToDelete] = useState(null);
  const [exporting, setExporting] = useState(false);

  const epoch = useRef(0);
  const clubRef = useRef(clubId);
  clubRef.current = clubId;
  const aliveRef = useRef(false);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);
  const changedRef = useRef(onClubChanged);
  changedRef.current = onClubChanged;

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!clubId) return;
    const my = ++epoch.current;
    const at = clubId;
    if (!silent) { setLoading(true); setError(""); }
    const { data, error: err } = await EventService.getEvents({ clubId: at }, { userClubIds: [at] });
    if (epoch.current !== my || clubRef.current !== at) return; // stale: newer request or club switched
    if (err) {
      if (!silent) setError(msg(err, "Failed to load events"));
    } else {
      setEvents(data || []);
    }
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    setEvents([]);
    setFilter("upcoming");
    setForm(null); setCreated(null); setQrEvent(null); setAttend(null); setToDelete(null);
    load();
    return () => { epoch.current += 1; };
  }, [clubId, load]);

  // Re-split upcoming/past every minute.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const { upcoming, past } = useMemo(() => {
    const up = [];
    const pa = [];
    for (const e of events) (isEventUpcoming(e) ? up : pa).push(e);
    up.sort(compareByStart);
    pa.sort(compareByStartDesc); // latest first, undated last
    return { upcoming: up, past: pa };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, tick]);

  const canDelete = (e) => !!uid && (e.creator_id === uid || club?.creator_id === uid);

  const onCreated = ({ events: made, extra, partial }) => {
    setEvents((prev) => upsert(prev, made));
    load({ silent: true });
    changedRef.current?.();
    if (!aliveRef.current) { // the tab was left mid-submit: nothing on screen to show the result, so say it here
      toast(partial ? "Event series partly created. Check the Events tab." : "Event created", partial ? "alert" : undefined);
      return;
    }
    if (partial) return; // the form stays open showing the partial-series error
    setForm(null);
    setCreated({ event: made[0], extra });
  };

  const onEdited = (id, patch, imageUpload) => {
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    setForm(null);
    load({ silent: true });
    if (imageUpload) {
      markPending(`event:${id}`); // Edit stays disabled until the photo attach lands, so it can't undo a newer edit
      imageUpload()
        .then((p) => { if (p) setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...p } : e))); })
        .catch((err) => toast(`Event saved, but the photo failed to upload: ${msg(err, "try again")}`, "alert"))
        .finally(() => clearPending(`event:${id}`));
    }
  };

  const confirmDelete = async () => {
    const target = toDelete;
    setToDelete(null);
    if (!target) return;
    // W1 rule: a write runs only as the account that started it.
    if (!signedInAs(uid)) { toast("You signed out or switched accounts.", "alert"); return; }
    const at = clubRef.current;
    let index = -1;
    setEvents((prev) => {
      index = prev.findIndex((e) => e.id === target.id);
      return prev.filter((e) => e.id !== target.id);
    });
    const { error: err } = await EventService.deleteEvent(target.id, uid);
    if (err) {
      if (clubRef.current === at) {
        setEvents((prev) => {
          if (prev.some((e) => e.id === target.id)) return prev;
          const next = [...prev];
          next.splice(index >= 0 ? Math.min(index, next.length) : next.length, 0, target);
          return next;
        });
      }
      toast(msg(err, "Failed to delete event"), "alert");
      return;
    }
    changedRef.current?.();
  };

  const exportSemester = async () => {
    setExporting(true);
    const { error: err } = await exportClubSemesterCsv(club.id, club.name);
    setExporting(false);
    if (err) toast(msg(err), "alert");
  };

  const closeQR = useCallback(() => setQrEvent(null), []);
  const refreshCounts = useCallback(() => load({ silent: true }), [load]);
  return {
    uid, loading, error, filter, setFilter, form, setForm, created, setCreated, qrEvent, setQrEvent,
    attend, setAttend, toDelete, setToDelete, exporting, upcoming, past, load, canDelete,
    onCreated, onEdited, confirmDelete, exportSemester, closeQR, refreshCounts,
  };
}
